import React, { useEffect, useState } from "react";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import AreaApi from "../../services/AreaApi";
import ProductionUnitApi from "../../services/ProductionUnitApi";
import IApiAreasResponse from "../../interfaces/IApiAreasResponse";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";

// Design System
import PageHeader from "../../components/PageHeader/PageHeader";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import DataTable from "../../components/DataTable/DataTable";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

const STORAGE_KEY = "ListAreas_filters";

const ListAreas: React.FC = () => {
  const navigate = useNavigate();
  const { plantId } = useAuth();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");
  const { t: translateMessages } = useTranslation("messages");

  const [filteredAreas, setFilteredAreas] = useState<IApiAreasResponse[]>([]);
  const [initialData, setInitialData] = useState<IApiAreasResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filters state
  const [filterName, setFilterName] = useState("");
  const [filterProductionUnitIds, setFilterProductionUnitIds] = useState<string[]>([]);
  const [productionUnits, setProductionUnits] = useState<any[]>([]);

  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  // Pagination/Sort state (Client-side)
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    fetchAreas();
    fetchProductionUnits();
  }, [plantId]);

  useEffect(() => {
    if (initialData.length > 0) {
      if (savedFilters.name) setFilterName(savedFilters.name.value);
      if (savedFilters.productionUnitIds) setFilterProductionUnitIds(savedFilters.productionUnitIds.value || []);
      applyFilters(savedFilters);
    }
  }, [initialData]);

  const fetchAreas = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await AreaApi.GetAllAreas();
      const fetched = response.data || [];
      setInitialData(fetched);
      setFilteredAreas(fetched);
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductionUnits = async (): Promise<void> => {
    try {
      const response = plantId 
        ? await ProductionUnitApi.GetProductUnitByPlant(plantId)
        : await ProductionUnitApi.GetAllProductUnit();
      const fetched = response.data || [];
      setProductionUnits(fetched.map((u: any) => ({
        id: u.id,
        label: u.name,
        description: u.name,
        value: u.id
      })));
    } catch (error) {
      console.error(error);
    }
  };

  const applyFilters = (filters: Record<string, any>): void => {
    let filtered = [...initialData];

    if (filters.name?.value) {
      filtered = filtered.filter((a) =>
        a.name.toLowerCase().includes(filters.name.value.toLowerCase())
      );
    }

    if (filters.productionUnitIds?.value?.length > 0) {
      filtered = filtered.filter((a) =>
        filters.productionUnitIds.value.includes(a.productionUnit?.id)
      );
    }

    setFilteredAreas(filtered);
    setPage(0);
  };

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      name: filterName ? { value: filterName } : undefined,
      productionUnitIds: filterProductionUnitIds.length > 0 ? { value: filterProductionUnitIds } : undefined,
    };

    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    applyFilters(filters);
  };

  const handleClearFilters = (): void => {
    setFilterName("");
    setFilterProductionUnitIds([]);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    setFilteredAreas(initialData);
    setPage(0);
  };

  const deleteArea = async (id: string): Promise<void> => {
    setIsLoading(true);
    try {
      await AreaApi.DeleteArea(id);
      displayToast("success", translateMessages("deleteSuccess"), "", false, 3000);
      fetchAreas();
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = (area: IApiAreasResponse): void => {
    confirmDialog({
      message: translateMessages("deleteArea"),
      header: translateGeneral("confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptLabel: translateForm("Yes"),
      rejectLabel: translateForm("No"),
      acceptClassName: "p-button-danger",
      rejectClassName: "p-button-secondary",
      accept: () => deleteArea(area.id),
    });
  };

  const handlePageChange = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
  };

  const handleSortChange = (e: any): void => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
  };

  const actionBodyTemplate = (rowData: IApiAreasResponse): JSX.Element => (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      <IconButton
        icon="visibility"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/areas/${rowData.id}`)}
        aria-label={translateGeneral("seeDetails")}
      />
      <IconButton
        icon="edit"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/areas/${rowData.id}/edit`)}
        aria-label={translateGeneral("edit")}
      />
      <IconButton
        icon="delete"
        variant="text"
        severity="secondary"
        onClick={() => confirmDelete(rowData)}
        aria-label={translateGeneral("delete")}
      />
    </div>
  );

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("area")}
        description={translateForm("areaDescription") || "Gerencie as áreas da planta e suas respectivas unidades de produção"}
      >
        <Button
          label={translateGeneral("register")}
          icon="add"
          severity="primary"
          onClick={() => navigate("/areas/create")}
        />
      </PageHeader>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <FilterPanel
          title={translateGeneral("filters") || "Filtros"}
          onApply={handleFilter}
          onClear={handleClearFilters}
        >
          <Input
            type="text"
            label={translateForm("name")}
            placeholder={translateForm("searchByName") || "Pesquisar por nome"}
            value={filterName}
            onChange={(val: any) => setFilterName(val)}
          />
          <Input
            type="multiselect"
            label={translateForm("productionUnit")}
            placeholder={translateForm("selectProductionUnits") || "Selecione as unidades de produção"}
            value={filterProductionUnitIds}
            options={productionUnits}
            onChange={(val: any) => setFilterProductionUnitIds(val)}
          />
        </FilterPanel>

        <DataTable
          value={filteredAreas}
          paginated
          first={page}
          rows={rows}
          totalRecords={filteredAreas.length}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[10, 20, 50]}
          loading={isLoading}
          sortField={sortField}
          sortOrder={sortOrder === "asc" ? 1 : -1}
          onSort={handleSortChange}
          emptyMessage={translateGeneral("noRecordsFound")}
        >
          <Column field="name" header={translateForm("name")} sortable />
          <Column field="productionUnit.name" header={translateForm("productionUnit")} sortable />
          <Column
            header={translateForm("actions")}
            body={actionBodyTemplate}
            style={{ width: "8rem" }}
          />
        </DataTable>
      </div>
      <ConfirmDialog />
    </div>
  );
};

export default ListAreas;
